import { useState } from 'react';
import { SectionType } from '../DataModal/DataModal.interface';
import { InfoCardData, ANDAInfoCardData } from 'Interfaces/ProductWikiInterfaces';
import { LossOfExclusivityInfoCardData } from 'Interfaces/ProductWikiInterfaces/landingPage.types';
import { ContractExpirationsSectionProps, PeriodOption } from './ContractExpirationsSection.type';
import { useStore } from 'hooks/useStore';
import { getColumnsBySectionType } from '../DataModal/modalColumnConfigs';
import { getSolicitationDetails } from 'services/productWikiServices';
import { SolicitationDetailsResponse } from '../SolicitationDetailModal/SolicitationDetailModal';

export const useContractExpirationsSection = ({ contracts }: ContractExpirationsSectionProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentSection, setCurrentSection] = useState<SectionType>('contractExpirations');
  const [conceptFilterTerm, setConceptFilterTerm] = useState<string>('');
  const [ndcFilterTerm, setNdcFilterTerm] = useState<string>('');
  const [showLoeDetailModal, setShowLoeDetailModal] = useState<boolean>(false);
  const [selectedLoeCard, setSelectedLoeCard] = useState<LossOfExclusivityInfoCardData | null>(
    null
  );
  // ANDA detail modal state
  const [showAndaDetailModal, setShowAndaDetailModal] = useState<boolean>(false);
  const [selectedAnda, setSelectedAnda] = useState<ANDAInfoCardData | null>(null);
  // Contract expiration detail modal state
  const [showContractDetailModal, setShowContractDetailModal] = useState<boolean>(false);
  const [selectedContract, setSelectedContract] = useState<InfoCardData | null>(null);
  // Solicitation detail modal state
  const [showSolicitationDetailModal, setShowSolicitationDetailModal] = useState<boolean>(false);
  const [solicitationDetails, setSolicitationDetails] =
    useState<SolicitationDetailsResponse | null>(null);
  const [isSolicitationDetailsLoading, setIsSolicitationDetailsLoading] = useState<boolean>(false);

  const {
    LandingPageStore: {
      fetchContractExpirations,
      fetchAndaUpdates,
      fetchLossOfExclusivity,
      isContractExpirationsLoading,
      isAndaUpdatesLoading,
      isLossOfExclusivityLoading,
      isPreSolicitationAlertsLoading,
      landingPageData,
      selectedContractExpirationsPeriod,
      selectedAndaUpdatesPeriod,
      selectedLossOfExclusivityPeriod,
    },
  } = useStore();

  const periodOptions: PeriodOption[] = [
    { label: 'Next 3 months', value: 90 },
    { label: 'Next 6 months', value: 180 },
    { label: 'Next 12 months', value: 365 },
  ];

  const andaPeriodOptions: PeriodOption[] = [
    { label: 'Past 3 months', value: 90 },
    { label: 'Past 6 months', value: 180 },
    { label: 'Past 12 months', value: 365 },
  ];

  const handlePeriodChange = async (e: { value: number | string }) => {
    const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
    // Fetch contract expirations with the selected period (store will update selectedContractExpirationsPeriod)
    await fetchContractExpirations(periodValue);
  };

  const handleAndaPeriodChange = async (e: { value: number | string }) => {
    const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
    await fetchAndaUpdates(periodValue);
  };

  const handleLoePeriodChange = async (e: { value: number | string }) => {
    const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
    await fetchLossOfExclusivity(periodValue);
  };

  // Get loading state for current section
  const getCurrentSectionLoading = (): boolean => {
    switch (currentSection) {
      case 'contractExpirations':
        return isContractExpirationsLoading;
      case 'andaUpdates':
        return isAndaUpdatesLoading;
      case 'lossOfExclusivity':
        return isLossOfExclusivityLoading;
      case 'preSolicitationAlerts':
        return isPreSolicitationAlertsLoading;
      default:
        return false;
    }
  };

  const displayedContracts = contracts.slice(0, 6);
  const hasMoreContracts = contracts.length > 6;

  // Get data for current section
  const getSectionData = (section: SectionType): InfoCardData[] => {
    if (!landingPageData) return [];

    switch (section) {
      case 'contractExpirations':
        return landingPageData.contractExpirations || [];
      case 'andaUpdates':
        return landingPageData.andaUpdates || [];
      case 'preSolicitationAlerts':
        return landingPageData.preSolicitationAlerts || [];
      case 'lossOfExclusivity':
        return landingPageData.lossOfExclusivity || [];
      // case 'marketIntelligence':
      //   return landingPageData.marketIntelligence || [];
      default:
        return [];
    }
  };

  // Handle section change in modal
  const handleSectionChange = (section: SectionType) => {
    setCurrentSection(section);
  };

  // Handle Contract Expiration card click to open detail modal
  const handleContractCardClick = (item: InfoCardData) => {
    setSelectedContract(item);
    setShowContractDetailModal(true);
  };

  // Handle LOE card click to open detail modal (for when user switches to LOE section)
  const handleLoeCardClick = (item: LossOfExclusivityInfoCardData) => {
    setSelectedLoeCard(item);
    setShowLoeDetailModal(true);
  };

  // Handle ANDA card click to open detail modal (for when user switches to ANDA section)
  const handleAndaCardClick = (item: ANDAInfoCardData) => {
    setSelectedAnda(item);
    setShowAndaDetailModal(true);
  };

  // Handle Solicitation card click to fetch details and open modal
  const handleSolicitationCardClick = async (item: InfoCardData) => {
    const solicitationNumber = item.solicitation_number;
    if (!solicitationNumber) {
      console.error('No solicitation number available for this item');
      return;
    }

    setIsSolicitationDetailsLoading(true);
    setShowSolicitationDetailModal(true);

    try {
      const response = await getSolicitationDetails(solicitationNumber);
      setSolicitationDetails(response.data);
    } catch (error) {
      console.error('Error fetching solicitation details:', error);
      setSolicitationDetails(null);
    } finally {
      setIsSolicitationDetailsLoading(false);
    }
  };

  // Handle row click based on current section
  const handleRowClick = (row: InfoCardData | ANDAInfoCardData | LossOfExclusivityInfoCardData) => {
    if (currentSection === 'contractExpirations') {
      handleContractCardClick(row as InfoCardData);
    } else if (currentSection === 'andaUpdates') {
      handleAndaCardClick(row as ANDAInfoCardData);
    } else if (currentSection === 'lossOfExclusivity') {
      handleLoeCardClick(row as LossOfExclusivityInfoCardData);
    } else if (currentSection === 'preSolicitationAlerts') {
      handleSolicitationCardClick(row as InfoCardData);
    }
  };

  // Track concept_name and ndcs column filter values so the detail modal can highlight matches
  const handleFiltersChange = (filtersState: Record<string, unknown>) => {
    const conceptFilter = filtersState['concept_name'] as { value: string } | undefined;
    setConceptFilterTerm(conceptFilter?.value ?? '');
    const ndcFilter = filtersState['ndcs'] as { value: string } | undefined;
    setNdcFilterTerm(ndcFilter?.value ?? '');
  };

  // Get columns for current section
  const columns = getColumnsBySectionType(currentSection);
  const modalData = getSectionData(currentSection);

  return {
    showModal,
    setShowModal,
    currentSection,
    setCurrentSection,
    columns,
    modalData,
    handlePeriodChange,
    handleAndaPeriodChange,
    handleLoePeriodChange,
    getCurrentSectionLoading,
    handleSectionChange,
    selectedContractExpirationsPeriod,
    selectedAndaUpdatesPeriod,
    selectedLossOfExclusivityPeriod,
    periodOptions,
    andaPeriodOptions,
    displayedContracts,
    hasMoreContracts,
    isContractExpirationsLoading,
    // Contract expiration detail modal state and handlers
    showContractDetailModal,
    setShowContractDetailModal,
    selectedContract,
    setSelectedContract,
    handleContractCardClick,
    // LOE detail modal state and handlers
    showLoeDetailModal,
    setShowLoeDetailModal,
    selectedLoeCard,
    setSelectedLoeCard,
    // ANDA detail modal state and handlers
    showAndaDetailModal,
    setShowAndaDetailModal,
    selectedAnda,
    setSelectedAnda,
    // Solicitation detail modal state and handlers
    showSolicitationDetailModal,
    setShowSolicitationDetailModal,
    solicitationDetails,
    setSolicitationDetails,
    isSolicitationDetailsLoading,
    // Combined row click handler
    handleRowClick,
    // Filter state for modal highlighting
    conceptFilterTerm,
    ndcFilterTerm,
    handleFiltersChange,
  };
};
